import { sanitizeAmbient } from "../../util/ambientSettings";
import ActionType from "../../resources/enums/index";
import storage from "../../util/storage";

/*
 * 🔴 THE SPLIT-BRAIN siteId (fixed 2026-09-04).
 *
 * There were TWO sources of truth for which site the dashboard is showing:
 *
 *   - Redux, `authData.site._id`, set the moment auth data lands. This is what
 *     AnalyticsView reads, which is why the LIVE view always worked.
 *   - localStorage `siteId`, read by THIRTEEN call sites across eight files -
 *     pagesModel, clusterService, metricsModal, playbackModal,
 *     onsiteReconciliation, segmentDetailModal, RightMenu - i.e. everything
 *     that FETCHES.
 *
 * localStorage was written in only five places: signup, payment, admin
 * impersonation and the subscription modal. NOT on login, and not on app
 * mount. So on a browser that had never signed up or paid - a fresh incognito
 * window, a new laptop, cleared site data - a customer could log in, see
 * "1 visitor onsite" from Redux, and get NOTHING from every historical query,
 * because those read an empty string.
 *
 * Reported on production, repeatedly, as "leaving the Skydive window open and
 * it not updating, requiring a refresh" - and the historical cluster bubbles,
 * the Sessions drawer and the footer were all empty together, which is the
 * tell: one shared input, not three separate bugs.
 *
 * Mirroring it here is a side effect in a reducer, which is not pure and is
 * chosen deliberately. This is the ONE place every authentication path funnels
 * through, and it runs SYNCHRONOUSLY before the re-render - so storage is
 * populated before any child component can read it. An effect in App.tsx would
 * run after children mount, which is exactly the race being closed.
 */
const syncSiteIdToStorage = (payload: any) => {
  const siteId = payload?.site?._id;

  if (siteId) {
    storage.set("siteId", siteId);
  }
};

/*
 * 🔴 KEEP THE SITE THE CUSTOMER CHOSE.
 *
 * getUserDto (backend) always returns `site` as the FIRST site the account
 * owns. Every LOGIN dispatch carries that, and the app refetches currentUser
 * on boot - so after switching sites the selection flashed on screen and was
 * immediately overwritten back to site[0]. Reported as "it flashes for a
 * second then goes back".
 *
 * The chosen site lives in storage.siteId, which is already this app's
 * cross-module record of which site is open. So on every LOGIN, if that id
 * names one of the sites the account actually owns, it wins over the
 * server's default.
 *
 * The ownership check is the important half: an id that is stale, tampered
 * with, or belongs to another account is IGNORED rather than trusted, and we
 * fall back to the server's answer. The backend's own membership guard would
 * reject it anyway, but the UI should never try.
 */
const preferChosenSite = (payload: any) => {
  const chosenId = storage.get("siteId");
  const owned = Array.isArray(payload?.sites) ? payload.sites : [];

  if (!chosenId || !owned.length) {
    return payload;
  }

  const chosen = owned.find((s: any) => String(s?._id) === String(chosenId));

  return chosen ? { ...payload, site: chosen } : payload;
};


export interface IRootState {
  isLoggedIn: boolean;
  isRegistered: string;

  userCount: number;
  emailValidated: boolean;
  historicalFilterSelected: { name: string; value: string };
}

export interface IRootAdminState {
  isLoggedIn: boolean;
}

const initialState: IRootState = {
  isLoggedIn: false,
  isRegistered: "",

  userCount: 0,
  emailValidated: false,
  historicalFilterSelected: { name: "Today", value: "" },
};

const initialAdminState: IRootAdminState = {
  isLoggedIn: false,
};

interface LoginAction {
  type: ActionType.LOGIN;
  payload: any;
}

interface LogoutAction {
  type: ActionType.LOGOUT;
  payload: any;
}

interface UpdateClusterPreferenceAction {
  type: ActionType.UPDATECLUSTER;
  payload: any;
}

interface AdminLoginAction {
  type: ActionType.ADMINLOGIN;
  payload: any;
}

interface SubscriptionTitle {
  type: ActionType.SUBSCRIPTIONTITLE;
  payload: any;
}

export type Action =
  | LoginAction
  | LogoutAction
  | UpdateClusterPreferenceAction
  | AdminLoginAction
  | SubscriptionTitle;

const AuthReducer = (state = initialState, action?: any) => {
  switch (action?.type) {
    case ActionType.LOGIN: {
      const payload = preferChosenSite(action.payload);
      syncSiteIdToStorage(payload);

      return {
        ...state,
        isLoggedIn: true,
        isClose: true,
        authData: payload,
        bubbleClusterParameters: {
          speed: payload.settings.speed,
          friction: payload.settings.friction,
          gravity: payload.settings.gravity,
          charge: payload.settings.charge,
          historicalCharge: payload.settings.historicalCharge,
          // Ambient life: made valid HERE, once, so no reader has to cope with a missing or unrecognised value
          ...sanitizeAmbient(payload.settings),
        },
      };
    }
    case ActionType.USER_LOGIN_BY_ADMIN: {
      syncSiteIdToStorage(action.payload);

      return {
        ...state,
        isLoggedIn: false,
        isClose: true,
        authData: action.payload,
        bubbleClusterParameters: {
          speed: action.payload.settings.speed,
          friction: action.payload.settings.friction,
          gravity: action.payload.settings.gravity,
          charge: action.payload.settings.charge,
          historicalCharge: action.payload.settings.historicalCharge,
          ...sanitizeAmbient(action.payload.settings),
        },
      };
    }
    case ActionType.ACTIVEUSERCOUNT: {
      return {
        ...state,
        isLoggedIn: true,
        userCount: action.payload,
      };
    }
    case ActionType.SUBSCRIPTIONDATA: {
      return {
        ...state,
        isLoggedIn: true,
        accountRecord: action.payload,
      };
    }

    // Merges into the existing authData rather than replacing it wholesale
    // (unlike LOGIN, which also resets bubbleClusterParameters from a full
    // settings payload) - a profile edit (name/email) only ever returns the
    // updated user document, not a full re-login payload.
    case ActionType.UPDATEPROFILE: {
      return {
        ...state,
        authData: {
          ...(state as any).authData,
          ...action.payload,
        },
      };
    }

    case ActionType.LOGOUT: {
      return {
        ...state,
        isLoggedIn: false,
        authData: "",
        accountRecord: "",
        subscriptionTitle: "",
        bubbleClusterParameters: {},
        userCount: 0,
        emailValidated: false,
        historicalFilterSelected: { name: "Today", value: "" },
      };
    }

    case ActionType.UPDATECLUSTER: {
      return {
        ...state,
        bubbleClusterParameters: action.payload,
      };
    }
    case ActionType.EMAILVALIDATED: {
      return {
        ...state,
        emailValidated: action.payload,
      };
    }
    case ActionType.SORTING_ORDER: {
      return {
        ...state,
        sortingOrder: action.payload,
      };
    }
    case ActionType.HISTORICAL_FILTER_SELECTED: {
      return {
        ...state,
        historicalFilterSelected: {
          name: action.payload.name,
          value: action.payload.value,
        },
      };
    }
    case ActionType.SUBSCRIPTIONTITLE: {
      return {
        ...state,
        subscriptionTitle: action.payload.title,
        // Annual pricing (2026-08-13) - the monthly/annual toggle in
        // PaymentModal dispatches this alongside the title so
        // changeSubscription/createSubscription can pass it straight
        // through to the backend. Defaults to 'monthly' so every existing
        // dispatch site that doesn't know about intervals yet still works.
        subscriptionBillingInterval: action.payload.billingInterval ?? "monthly",
      };
    }
    case ActionType.TOTALSESSION: {
      return {
        ...state,
        totalSession: action.payload.totalSession,
      };
    }
    case ActionType.CLOSEMODEL: {
      return {
        ...state,
        isClose: action.payload.isClose,
      };
    }

    default:
      return state;
  }
};

export const AdminAuthReducer = (state = initialAdminState, action?: any) => {
  switch (action?.type) {
    case ActionType.ADMINLOGIN: {
      return {
        ...state,
        isLoggedIn: true,
        adminauthData: action.payload,
      };
    }

    case ActionType.ADMINLOGOUT: {
      return {
        ...state,
        isLoggedIn: false,
        adminauthData: "",
      };
    }
    default:
      return state;
  }
};

export default AuthReducer;
